import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '@/api/axios';
import { useAuth } from '@/context/AuthContext';

const MenuContext = createContext(null);

// 파생 시 매번 새 배열/객체를 만들면 하위 useMemo 가 전부 무효화되므로 고정 참조를 쓴다.
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

/**
 * 백엔드가 camelCase / PascalCase 중 무엇으로 직렬화하든 동일하게 다루기 위한 정규화.
 * (기존 Sidebar 가 `menu.children || menu.Children` 로 방어하던 로직을 여기로 일원화)
 */
const normalizeNode = (raw, parent = null) => {
  const node = {
    menuId: raw.menuId ?? raw.MenuId,
    menuCode: raw.menuCode ?? raw.MenuCode ?? null,
    moduleCode: raw.moduleCode ?? raw.ModuleCode ?? null,
    parentId: raw.parentId ?? raw.ParentId ?? null,
    menuName: raw.menuName ?? raw.MenuName ?? '',
    urlPath: raw.urlPath ?? raw.UrlPath ?? null,
    iconClass: raw.iconClass ?? raw.IconClass ?? null,
    badgeKey: raw.badgeKey ?? raw.BadgeKey ?? null,
    isDisplay: (raw.isDisplay ?? raw.IsDisplay) !== false,
    isMobileVisible: (raw.isMobileVisible ?? raw.IsMobileVisible) !== false,
    sortOrder: raw.sortOrder ?? raw.SortOrder ?? 0,
    depth: raw.depth ?? raw.Depth ?? 0,
    parent,
  };
  const rawChildren = raw.children ?? raw.Children ?? [];
  node.children = rawChildren.map((child) => normalizeNode(child, node));
  return node;
};

/** 트리를 평탄화 (검색 / 경로 매칭용) */
const flattenTree = (nodes, acc = []) => {
  nodes.forEach((node) => {
    acc.push(node);
    if (node.children?.length) flattenTree(node.children, acc);
  });
  return acc;
};

/**
 * 현재 pathname 에 해당하는 메뉴를 찾는다.
 * 가장 긴(=가장 구체적인) urlPath 가 이긴다.
 * 세그먼트 경계를 검사하므로 `/projects/task` 가 `/projects/tasks` 에 오탐되지 않는다.
 */
const findMenuByPath = (flatMenus, pathname) => {
  let best = null;
  flatMenus.forEach((menu) => {
    if (!menu.urlPath) return;
    const path = menu.urlPath;
    if (pathname !== path && !pathname.startsWith(`${path}/`)) return;
    if (!best || path.length > best.urlPath.length) best = menu;
  });
  return best;
};

export const MenuProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  const [rawMenuTree, setMenuTree] = useState(EMPTY_ARRAY);
  const [rawCodeMap, setCodeMap] = useState(EMPTY_ARRAY);
  const [rawFavorites, setFavorites] = useState(EMPTY_ARRAY);
  const [rawBadges, setBadges] = useState(EMPTY_OBJECT);
  const [isLoading, setIsLoading] = useState(true);

  // ── 데이터 로드 ────────────────────────────────────────────────
  const fetchMenus = useCallback(async () => {
    setIsLoading(true);
    try {
      const [treeRes, codeRes, favRes] = await Promise.allSettled([
        api.get('/menus'),
        api.get('/menus/codes'),
        api.get('/menus/favorites'),
      ]);

      if (treeRes.status === 'fulfilled' && Array.isArray(treeRes.value.data)) {
        setMenuTree(treeRes.value.data.map((node) => normalizeNode(node)));
      }
      if (codeRes.status === 'fulfilled' && Array.isArray(codeRes.value.data)) {
        setCodeMap(codeRes.value.data.map((m) => normalizeNode(m)));
      }
      if (favRes.status === 'fulfilled' && Array.isArray(favRes.value.data)) {
        setFavorites(favRes.value.data.map((m) => normalizeNode(m)));
      }
    } catch (error) {
      console.error('[MenuContext] 메뉴 로드 실패:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshBadges = useCallback(async () => {
    try {
      const res = await api.get('/menus/badges');
      setBadges(res.data || {});
    } catch {
      // 배지는 부가 정보이므로 실패해도 화면을 막지 않는다.
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    fetchMenus();
    refreshBadges();
    return undefined;
  }, [isAuthenticated, fetchMenus, refreshBadges]);

  // ── 파생 상태 ──────────────────────────────────────────────────
  // 로그아웃 상태에서는 상태를 초기화하는 대신 빈 값으로 파생시킨다.
  // (이전 사용자의 메뉴가 한 프레임이라도 노출되는 것을 원천 차단)
  const menuTree = isAuthenticated ? rawMenuTree : EMPTY_ARRAY;
  const codeMap = isAuthenticated ? rawCodeMap : EMPTY_ARRAY;
  const favorites = isAuthenticated ? rawFavorites : EMPTY_ARRAY;
  const badges = isAuthenticated ? rawBadges : EMPTY_OBJECT;
  // 비로그인 상태에서는 애초에 요청을 보내지 않으므로 로딩 중이 아니다.
  const isMenuLoading = isAuthenticated && isLoading;

  const flatMenus = useMemo(() => flattenTree(menuTree), [menuTree]);

  /** menuCode → menu (권한 무관 전체 목록 기준. 라우트 가드가 id 해석에 사용) */
  const menuByCode = useMemo(() => {
    const map = new Map();
    codeMap.forEach((menu) => { if (menu.menuCode) map.set(menu.menuCode, menu); });
    return map;
  }, [codeMap]);

  /** GNB 모듈 = 권한 필터링된 트리의 최상위 노드들 */
  const modules = useMemo(
    () => menuTree.filter((node) => node.isDisplay),
    [menuTree]
  );

  /** 현재 경로에 해당하는 메뉴 */
  const activeMenu = useMemo(
    () => findMenuByPath(flatMenus, location.pathname),
    [flatMenus, location.pathname]
  );

  /** 현재 모듈 = activeMenu 의 최상위 조상 (없으면 첫 모듈) */
  const activeModule = useMemo(() => {
    let node = activeMenu;
    while (node?.parent) node = node.parent;
    return node || modules[0] || null;
  }, [activeMenu, modules]);

  /** 브레드크럼: 최상위 → 현재 */
  const breadcrumb = useMemo(() => {
    const trail = [];
    let node = activeMenu;
    while (node) { trail.unshift(node); node = node.parent; }
    return trail;
  }, [activeMenu]);

  const favoriteIds = useMemo(
    () => new Set(favorites.map((f) => f.menuId)),
    [favorites]
  );

  // ── 즐겨찾기 조작 ──────────────────────────────────────────────
  const toggleFavorite = useCallback(async (menu) => {
    if (!menu?.menuId) return;
    const isFav = favoriteIds.has(menu.menuId);

    // 낙관적 갱신 후 실패 시 서버 상태로 되돌린다.
    setFavorites((prev) =>
      isFav ? prev.filter((f) => f.menuId !== menu.menuId) : [...prev, menu]
    );

    try {
      if (isFav) await api.delete(`/menus/favorites/${menu.menuId}`);
      else await api.post(`/menus/favorites/${menu.menuId}`);
    } catch (error) {
      console.error('[MenuContext] 즐겨찾기 갱신 실패:', error);
      try {
        const res = await api.get('/menus/favorites');
        setFavorites((res.data || []).map((m) => normalizeNode(m)));
      } catch { /* noop */ }
    }
  }, [favoriteIds]);

  const isFavorite = useCallback((menuId) => favoriteIds.has(menuId), [favoriteIds]);

  /** menuCode 로 menuId 해석. 못 찾으면 null */
  const resolveMenuId = useCallback(
    (menuCode) => menuByCode.get(menuCode)?.menuId ?? null,
    [menuByCode]
  );

  const value = useMemo(() => ({
    menuTree,
    flatMenus,
    modules,
    activeMenu,
    activeModule,
    breadcrumb,
    favorites,
    isFavorite,
    toggleFavorite,
    badges,
    refreshBadges,
    menuByCode,
    resolveMenuId,
    isLoading: isMenuLoading,
    reloadMenus: fetchMenus,
  }), [
    menuTree, flatMenus, modules, activeMenu, activeModule, breadcrumb,
    favorites, isFavorite, toggleFavorite, badges, refreshBadges,
    menuByCode, resolveMenuId, isMenuLoading, fetchMenus,
  ]);

  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>;
};

export const useMenu = () => {
  const context = useContext(MenuContext);
  // MenuProvider 밖(로그인 화면 등)에서 호출되어도 터지지 않도록 빈 기본값을 준다.
  return context || {
    menuTree: [], flatMenus: [], modules: [], activeMenu: null, activeModule: null,
    breadcrumb: [], favorites: [], isFavorite: () => false, toggleFavorite: () => {},
    badges: {}, refreshBadges: () => {}, menuByCode: new Map(),
    resolveMenuId: () => null, isLoading: false, reloadMenus: () => {},
  };
};

export default MenuContext;
